import { inject } from "@angular/core";
import { Route, Router, Routes } from "@angular/router";
import { ApplicationIds } from "@dcc/shared/configs/vo-applications";
import { featuresGuard } from "@dcc/shared/feature-flags/data-access";
import { runSerially } from "@dcc/shared/util-routing";
import { applicationAllowedGuard, userLoadedGuard } from "@dcc/vestas-online/data-access";
import { config } from "@dcc/vestas-online/domain";
import { titleResolver, translate } from "@dcc/vestas-online/i18n/transloco-config";
import { maintenanceGuard, maintenancePlatformGuard } from "@dcc/vestas-online/shared/maintenance-mode/data-access";
import { onBoardUser } from "@dcc/vestas-online/user/onboarding/routing";
import { autoLoginPartialRoutesGuard } from "angular-auth-oidc-client";
import { ErrorComponent } from "./error/error.component";
import { termsGuard } from "./guards/terms.guard";
import { ShellComponent } from "./shell/shell.component";
import { TosComponent } from "./tos/tos.component";

const maintenancePlatformCheck = (path: string, children: Routes) => {
    return {
        path,
        canActivateChild: [maintenancePlatformGuard()],
        children
    };
};

const authCheck = (path: string, children: Routes) => {
    return {
        path,
        canActivate: [autoLoginPartialRoutesGuard],
        children
    };
};

export const routes: Routes = [
    {
        path: "",
        pathMatch: "full",
        title: "VestasOnline",
        loadComponent: () => import("@dcc/vestas-online/public/feat-landing").then((lib) => lib.LandingPageComponent),
        data: {
            pageView: {
                area: "Public",
                name: "Landing"
            }
        }
    },
    {
        path: "feedback",
        title: "Feedback form - VestasOnline",
        loadChildren: () => import("@dcc/vestas-online/public/feat-feedback-form").then((m) => m.routes),
        data: {
            pageView: {
                area: "Public"
            }
        }
    },
    {
        path: "support",
        title: "Support form - VestasOnline",
        loadChildren: () => import("@dcc/vestas-online/public/feat-support-form").then((m) => m.routes),
        data: {
            pageView: {
                area: "Public"
            }
        }
    },
    {
        path: "request-access",
        title: "Request access - VestasOnline",
        loadChildren: () => import("@dcc/vestas-online/public/feat-request-access").then((m) => m.routes),
        data: {
            pageView: {
                area: "Public"
            }
        }
    },
    maintenancePlatformCheck("app", [
        {
            path: "error",
            title: "Error - VestasOnline",
            component: ErrorComponent,
            data: {
                pageView: {
                    area: "Vestas Online",
                    name: "Error"
                }
            }
        },
        authCheck("", [
            {
                path: "",
                canActivateChild: [userLoadedGuard()],
                children: [
                    {
                        path: "terms",
                        title: titleResolver([translate("termsOfUse")]),
                        component: TosComponent,
                        data: {
                            pageView: {
                                area: "Vestas Online",
                                name: "Terms of use"
                            }
                        }
                    },
                    {
                        path: "user-setup",
                        title: titleResolver([translate("setupUser")]),
                        loadComponent: () => import("@dcc/vestas-online/user/onboarding/feat").then((lib) => lib.OnboardUserComponent),
                        data: {
                            pageView: {
                                area: "Vestas Online",
                                name: "Onboarding"
                            }
                        }
                    },
                    {
                        path: "maintenance",
                        title: titleResolver([translate("scheduledMaintenance")]),
                        loadComponent: () =>
                            import("@dcc/vestas-online/shared/maintenance-mode/feat").then((lib) => lib.MaintenancePageComponent),
                        data: {
                            pageView: {
                                area: "Vestas Online",
                                name: "Scheduled maintenance"
                            }
                        }
                    },
                    {
                        path: "",
                        component: ShellComponent,
                        canActivate: [runSerially([termsGuard(), onBoardUser()])],
                        children: [
                            {
                                path: "",
                                pathMatch: "full",
                                redirectTo: "jumpboard"
                            },
                            {
                                path: "jumpboard",
                                title: titleResolver([translate("voTextJumpboard")]),
                                loadComponent: () =>
                                    import("@dcc/vestas-online/feat-dashboard").then(({ DashboardComponent }) => DashboardComponent),
                                data: {
                                    applicationId: "jumpboard",
                                    name: "home",
                                    pageView: {
                                        area: "Jumpboard"
                                    }
                                }
                            },
                            {
                                path: "concept-exploration",
                                title: "Concept Exploration - VestasOnline",
                                loadComponent: () =>
                                    import("@dcc/vestas-online/concept-exploration").then((m) => m.ConceptExplorationComponent),
                                data: {
                                    applicationId: "concept-exploration",
                                    name: "ceNewConcept"
                                }
                            },
                            {
                                path: "planning-and-maintenance",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.ServicePlanning),
                                    maintenanceGuard(ApplicationIds.ServicePlanning)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/apps/planning-and-maintenance/shell").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.ServicePlanning,
                                    name: "planningAndMaintenance",
                                    pageView: {
                                        area: "Planning and Maintenance"
                                    }
                                }
                            },
                            {
                                path: "serviceorderreports/:id",
                                canMatch: [
                                    /**
                                     * This particular guard is here to redirect users to the new pam area if they have the feature flag enabled.
                                     * This ensures that users with the feature flag enabled are redirected to the new area coming from links in emails for SOR/SVS
                                     */
                                    (_: Route, state) => {
                                        const router = inject(Router);
                                        const currentNavigation = router.currentNavigation();
                                        const referrer = currentNavigation?.extractedUrl?.queryParams["referrer"];
                                        const id = state[1]?.path; // Extract the ':id' parameter

                                        if (id) {
                                            return router.navigate(["/app/planning-and-maintenance/service-reports"], {
                                                queryParams: { referrer: referrer ?? null, orderNumber: id, siteId: "all" }
                                            });
                                        }
                                        return router.navigate(["/app/planning-and-maintenance/service-reports"]);
                                    }
                                ],
                                children: []
                            },
                            {
                                path: "serviceschedule",
                                canMatch: [
                                    /**
                                     * This particular guard is here to redirect users to the new pam area if they have the feature flag enabled.
                                     * This ensures that users with the feature flag enabled are redirected to the new area coming from links in emails for SOR/SVS
                                     */
                                    () => {
                                        const router = inject(Router);
                                        const currentNavigation = router.currentNavigation();
                                        const referrer = currentNavigation?.extractedUrl?.queryParams["referrer"];
                                        const searchTerm = currentNavigation?.extractedUrl?.queryParams["searchTerm"];

                                        if (searchTerm) {
                                            return router.navigate(["/app/planning-and-maintenance/schedule"], {
                                                queryParams: { referrer: referrer ?? null, orderNumber: searchTerm }
                                            });
                                        }
                                        return router.navigate(["/app/planning-and-maintenance/schedule"]);
                                    }
                                ],
                                children: []
                            },
                            {
                                path: "subscriptions",
                                loadChildren: () => import("@dcc/vestas-online/user/email-notifications/feat").then((m) => m.routes),
                                data: {
                                    applicationId: "subscriptions",
                                    name: "voTextApplications",
                                    pageView: {
                                        area: "Account"
                                    }
                                }
                            },
                            {
                                path: "myaccount",
                                loadChildren: () => import("@dcc/vestas-online/user/feat-profile").then((m) => m.routes),
                                data: {
                                    applicationId: "myaccount",
                                    name: "myaccTextMyAccount",
                                    pageView: {
                                        area: "Account"
                                    }
                                }
                            },
                            {
                                path: "workspace",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.Workspaces),
                                    maintenanceGuard(ApplicationIds.Workspaces)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/apps/workspaces/feat-root").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.Workspaces,
                                    name: config[ApplicationIds.Workspaces].name,
                                    pageView: {
                                        area: "Workspaces"
                                    }
                                }
                            },

                            {
                                path: "finances",
                                canActivate: [applicationAllowedGuard(ApplicationIds.Invoices), maintenanceGuard(ApplicationIds.Invoices)],
                                loadChildren: async () => (await import("@dcc/vestas-online/finance/feat-shell")).routes,
                                data: {
                                    applicationId: ApplicationIds.Invoices,
                                    name: config[ApplicationIds.Invoices].name,
                                    pageView: {
                                        area: "Finance"
                                    }
                                }
                            },
                            {
                                path: "bam",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.BladeAssetManagement),
                                    maintenanceGuard(ApplicationIds.BladeAssetManagement)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/apps/blade-asset-management/shell").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.BladeAssetManagement,
                                    name: config[ApplicationIds.BladeAssetManagement].name
                                }
                            },
                            {
                                path: "technical-library",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.TechnicalLibrary),
                                    maintenanceGuard(ApplicationIds.TechnicalLibrary)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/apps/technical-library/shell").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.TechnicalLibrary,
                                    name: config[ApplicationIds.TechnicalLibrary].name,
                                    pageView: {
                                        area: "Technical Library"
                                    }
                                }
                            },
                            {
                                path: "toolkit",
                                title: titleResolver([translate("toolkit")]),
                                canActivate: [applicationAllowedGuard(ApplicationIds.Toolkit), maintenanceGuard(ApplicationIds.Toolkit)],
                                loadComponent: () => import("@dcc/vestas-online/apps/toolkit/feat").then((m) => m.RootComponent),
                                data: {
                                    applicationId: ApplicationIds.Toolkit,
                                    name: config[ApplicationIds.Toolkit].name,
                                    pageView: {
                                        area: "Toolkit",
                                        name: "Toolkit"
                                    }
                                }
                            },
                            {
                                path: "sales-documentation",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.ProjectPlanningPortal),
                                    maintenanceGuard(ApplicationIds.ProjectPlanningPortal)
                                ],
                                loadChildren: () =>
                                    import("@dcc/vestas-online/apps/project-planning-portal/feat-documents-shell").then(
                                        ({ routes }) => routes
                                    ),
                                data: {
                                    applicationId: ApplicationIds.ProjectPlanningPortal,
                                    name: config[ApplicationIds.ProjectPlanningPortal].name
                                }
                            },
                            {
                                path: "monthly-report-prototype",
                                canActivate: [featuresGuard("monthly-report-prototype")],
                                loadChildren: () => import("@dcc/vestas-online/apps/monthly-report/prototype/shell").then((m) => m.routes),
                                data: {
                                    applicationId: "monthly-report-prototype",
                                    name: "monthlyReports",
                                    pageView: {
                                        area: "Monthly Reports"
                                    }
                                }
                            },
                            {
                                path: "monthly-report",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.MonthlyReports),
                                    maintenanceGuard(ApplicationIds.MonthlyReports)
                                ],
                                loadChildren: () => import("@dcc/monthly-report/shell").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.MonthlyReports,
                                    name: config[ApplicationIds.MonthlyReports].name,
                                    pageView: {
                                        area: "Monthly Reports"
                                    }
                                }
                            },
                            {
                                path: "support",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.CaseManagement),
                                    maintenanceGuard(ApplicationIds.CaseManagement)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/areas/support/feat-overview").then(({ routes }) => routes),
                                data: {
                                    applicationId: ApplicationIds.CaseManagement,
                                    name: "caseManagementName",
                                    pageView: {
                                        area: "Support"
                                    }
                                }
                            },
                            {
                                path: "software-management",
                                canActivate: [
                                    applicationAllowedGuard(ApplicationIds.SoftwareManagement),
                                    maintenanceGuard(ApplicationIds.SoftwareManagement)
                                ],
                                loadChildren: () => import("@dcc/vestas-online/areas/software-management/shell").then((m) => m.routes),
                                data: {
                                    applicationId: ApplicationIds.SoftwareManagement,
                                    name: "softwareManagement",
                                    pageView: {
                                        area: "Software Management"
                                    }
                                }
                            }
                        ]
                    }
                ]
            }
        ]),
        {
            path: "**",
            redirectTo: "/app/jumpboard"
        }
    ]),
    {
        path: "platform-maintenance",
        title: "Scheduled platform maintenance - VestasOnline",
        loadComponent: () =>
            import("./platform-maintenance/platform-maintenance-page.component").then((lib) => lib.PlatformMaintenancePageComponent),
        data: {
            pageView: {
                area: "Vestas Online",
                name: "Platform maintenance"
            }
        }
    },
    {
        path: "**",
        redirectTo: "/"
    }
];
